import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { Sparkles } from 'lucide-react';
import { PageLayout } from '../../../components/ui/PageLayout';
import Button from '../../../components/ui/Button';
import AppIcon from '../../../components/AppIcon';
import { getCompanySummaries, type CompanySummary } from '../../../api/Navigation/moduleConfig';
import EngagementJobsTab from '../Notifications/components/EngagementJobsTab';

/**
 * Owner decision §14.1c/11 — per-tenant Engagement job configuration on an OPERATOR surface.
 *
 * 🔴 CITE THE SECTION, NEVER A BARE ORDINAL. Three numbering schemes coexist in this project and
 * "decision 11" means three different things. Everything below is §14.1c/<n>.
 *
 * 🔴 THE MECHANISM ALREADY EXISTS AND IS REUSED WHOLE. CO-240 shipped the columns
 * (20260817041042_CO240AddEngagementJobCompanyToggles), the entity (CompanyRules.cs:131-133,
 * all three `= false`), the write path (CompanyService.UpdateCompanyRules), the job gating
 * (Q12NudgeJob.cs:102, RecognitionGapReminderJob.cs:90, CheckInReminderJob.cs:76) and the HR UI
 * (Notifications/components/EngagementJobsTab.tsx). §14.1c.1 says the delta is "a role gate and a
 * company picker", and that is all this file is. There is NO second mechanism, no new endpoint, no
 * new column, no migration and no new translation key.
 *
 * 🔴 THE FLAGS STAY ON CompanyRules, WHICH FAILS CLOSED. They are deliberately NOT routed through
 * the navigation-config table, which is default-ENABLED and fails OPEN (CompanyModuleConfig.cs:23,
 * CompanyPageConfig.cs:26, moduleConfig.ts). For a JOB, failing open means sending mail on tenants
 * nobody enabled: the CO-356 waste class. All three sweeps gate on a POSITIVE EXISTS over
 * CompanyRules, so a company with no row is structurally skipped, and nothing on this page can
 * change that. The nav row that reaches this page IS nav-config-gated, and only in the harmless
 * direction: switch it off and the operator loses a link, no flag changes value, and the jobs keep
 * doing exactly what the stored flags already said.
 *
 * ⚠️ IT DOES NOT CLOSE W3-35. A per-tenant skip changes which companies a loaded, scheduled job
 * ACTS ON. W3-35 asks whether the GLOBAL switch removes scheduler rows whose job class no longer
 * loads. Two different controls. Do not describe this page as closing that incident class.
 *
 * Its own route rather than a tab on Settings/ModuleConfig: that page is the UI of the
 * navigation-config table, and putting a CompanyRules-backed job switch among controls that all
 * fail open invites exactly the conflation above. Its own route rather than a section inside
 * Settings/Companies/OperatorCompanyRules.tsx: that page merges the fetched view into prior state
 * (`setInput(prev => ({ ...prev, ...res.payload, companyId }))`, OperatorCompanyRules.tsx:180) and
 * holds one load-time snapshot behind one Save button, so two forms over one row would be a
 * lost-update generator. A standalone route gets a fresh GET per selection, one form, one save.
 *
 * ── OWNER DECISIONS APPLIED HERE ─────────────────────────────────────────────────────────────────
 *
 * §14.1c/14 — THE RE-GET-BEFORE-POST STAYS, ON BOTH SURFACES. EngagementJobsTab.handleSave re-reads
 * CompanyRules immediately before the POST and overlays only the four booleans onto that fresh row,
 * on the HR tab as well as here. The owner accepted the new failure mode it introduces (a save can
 * now fail on the re-GET, after the click) in exchange for closing the lost-update window for HR
 * too. Do not revert it, and do not re-litigate it as "operator-only scope creep".
 *
 * §14.1c/15 — Company.EnableAINudges IS OPERATOR-WRITABLE. The read-only lock this page originally
 * invented is REVERSED, and the `aiNudgesReadOnly` prop that carried it is GONE from
 * EngagementJobsTab: the tab takes `companyId` alone and leaves the master switch live.
 * ⚠️ Chosen against the smaller blast radius, with the consequence understood: EnableAINudges
 * defaults TRUE on Company and AND-gates both AI senders, so one click here can un-mute — or mute —
 * a whole tenant's AI-nudge family cross-tenant. The mitigation is that the consequence is MARKED at
 * the switch (see the call site), never a lock. Do not reintroduce the prop.
 *
 * §14.1c/16 — THE QUARTZ SIDE EFFECT IS SUPPRESSED FOR FLAG-ONLY SAVES, SERVER-SIDE, AND THE
 * SUPPRESSION IS ASYMMETRIC. Read the corrected description of the side effect below; it is the
 * reason a server change was taken during the freeze rather than shipped-and-documented.
 *
 * 🔴 CORRECTION — THIS PAGE DOES TOUCH SCHEDULER ROWS. An earlier version of this comment claimed
 * "a job flag must never ride a payload whose handler has delete-and-reinsert semantics" and implied
 * this page was clear of that. It is not. Every save here goes to the ONE implementation behind both
 * POST /Company/UpdateCompanyRules and POST /Company/OperatorUpdateCompanyRules —
 * CompanyService.UpdateCompanyRules (CompanyService.cs:1156) — which, after SaveChangesAsync and
 * unconditionally, either calls CreateUsersMissedClockInNotificationJobAndTrigger or
 * RemoveUsersMissedClockInNotificationJobAndTrigger for the TARGET tenant, branching on that
 * tenant's stored ShiftFlowRuleType. The create path ends in
 * `scheduler.ScheduleJob(job, triggers, replace: true)` with a `StartNow()` trigger
 * (QuartzSingleton.CreateUsersMissedClockInNotificationJobAndTrigger), so a flag-only save REPLACES
 * `Trig-UsersMissedClockInNotification-Company-{companyId}` (CO-566 namespaced this key; it was
 * `Trig-UsersMissedClockInNotification-{companyId}`) and restarts its schedule, and the else-branch
 * DELETES the job outright. That is delete-and-reinsert, on an unrelated attendance job, in another
 * tenant, triggered by toggling an engagement switch.
 *
 * ⚠️ §14.1c/16 MITIGATES IT WITHOUT ELIMINATING IT, AND THE REMAINING CASE IS THE ONE TO KNOW.
 * The guard as written (CompanyService.cs, the `if / else if / else` after SaveChangesAsync)
 * transition-gates only the DELETE: the Remove now runs only when the STORED ShiftFlowRuleType was
 * BoundByWorkingHours and the saved one is not, and a save that moves nothing logs the suppression
 * so "no scheduler write" and "guard not wired up" stay distinguishable. The CREATE is deliberately
 * NOT gated, because that same call is the only thing in the codebase that ever creates, refreshes
 * or repairs the job. So on a tenant whose ShiftFlowRuleType IS BoundByWorkingHours, a flag-only
 * save from this page STILL rewrites that tenant's trigger with `StartNow()`. Do not describe this
 * page as free of scheduler writes; describe it as free of the DELETE. Keeping the save on its own
 * request and its own button is still right, but it was never what made this safe — the server guard
 * is, and only that far. A later "consolidate the saves" refactor must not undo either half.
 */
export default function OperatorEngagementJobs() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const [companies, setCompanies] = useState<CompanySummary[]>([]);
  const [loadingCompanies, setLoadingCompanies] = useState(true);
  /**
   * 🔴 THE PAGE MUST NOT CONFUSE "NO COMPANIES" WITH "NO ANSWER". This empty state is the page's
   * only feedback channel, and it used to read "No companies found. Only Operators can access this
   * page." for every outcome, because getCompanySummaries collapsed a 500, a timeout and a dropped
   * connection into `[]`. That told an operator they were not an operator. The helper now returns
   * `{ companies, failure, status }` and records the reason on the console when its catch fires;
   * `failure` is non-null ONLY when the request did not come back clean, and it takes over the whole
   * body so the ambiguous empty list is never rendered on a failed fetch.
   *
   * Three outcomes, three sentences, none of them borrowed from another:
   *   failure + 401/403  → "You don't have permission to perform this action." (no Retry: the answer
   *                        will not change), which is the ONLY case the old copy described.
   *   failure otherwise  → "Failed to load. Please try again." + Retry.
   *   success + empty    → "No companies found" — a fact, with no claim about who the reader is.
   */
  const [loadFailure, setLoadFailure] = useState<string | null>(null);
  const [loadStatus, setLoadStatus] = useState<number | null>(null);
  const [reloadNonce, setReloadNonce] = useState(0);
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoadingCompanies(true);
    (async () => {
      const result = await getCompanySummaries();
      if (cancelled) return;
      setCompanies(result.companies);
      setLoadFailure(result.failure);
      setLoadStatus(result.status);
      setLoadingCompanies(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [reloadNonce]);

  // Deep link, same `?companyId=` shape Settings/Companies/index.tsx already uses to jump straight
  // to a tenant's Company Rules, so a row action can later point here without a new convention.
  // It also restores the selection after a successful Retry: the query param outlives the failure.
  useEffect(() => {
    const qp = searchParams.get('companyId');
    if (!qp || companies.length === 0) return;
    const id = Number(qp);
    if (companies.some((c) => c.companyId === id)) {
      setSelectedCompanyId(id);
    }
  }, [searchParams, companies]);

  const handleCompanySelect = useCallback(
    (value: string) => {
      const id = value ? Number(value) : null;
      setSelectedCompanyId(id);
      if (id === null) {
        setSearchParams({});
      } else {
        setSearchParams({ companyId: String(id) });
      }
    },
    [setSearchParams]
  );

  // An error with no way out is a dead end, and this one is usually transient. `loadFailure` is
  // deliberately left standing until the retry lands, so the panel does not blink out and back.
  const handleRetry = useCallback(() => setReloadNonce((n) => n + 1), []);

  if (loadFailure !== null) {
    // 401/403 is the ONE outcome the old copy actually described, and the only one a retry cannot
    // help. Read from the transport status, never the body: an ASP.NET authorization denial is
    // bodiless and this server's Result.Status self-reports 200 regardless (CO-56).
    const refused = loadStatus === 401 || loadStatus === 403;
    return (
      <PageLayout title={t('Engagement Jobs')} icon={Sparkles} accentColor="slate" maxWidth="5xl">
        {/* Same destructive panel EngagementJobsTab uses for its own load error, so the two surfaces
            read as one system. Every key resolves in en, ar and fr (common.json), no new strings. */}
        <div
          role="alert"
          className="flex flex-wrap items-center gap-3 rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive"
        >
          <AppIcon name="AlertCircle" size={16} className="shrink-0" />
          <span className="min-w-0 flex-1">
            {refused
              ? t("You don't have permission to perform this action.")
              : t('Failed to load. Please try again.')}
          </span>
          {!refused && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleRetry}
              disabled={loadingCompanies}
              loading={loadingCompanies}
            >
              {t('Retry')}
            </Button>
          )}
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout title={t('Engagement Jobs')} icon={Sparkles} accentColor="slate" maxWidth="5xl">
      {/* Company selector. Markup and copy taken verbatim from Settings/ModuleConfig/index.tsx so the
          two Operator pickers behave identically; all three strings already resolve in en, ar and fr. */}
      <div className="mb-6">
        <label
          htmlFor="operator-engagement-jobs-company"
          className="mb-2 block text-sm font-medium text-foreground"
        >
          {t('Select Company')}
        </label>
        <select
          id="operator-engagement-jobs-company"
          className="w-full max-w-md rounded-lg border border-border bg-card px-3 py-2 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          value={selectedCompanyId ?? ''}
          onChange={(e) => handleCompanySelect(e.target.value)}
          disabled={loadingCompanies}
        >
          <option value="">{t('Select a company...')}</option>
          {companies.map((c) => (
            <option key={c.companyId} value={c.companyId}>
              {c.companyName}
            </option>
          ))}
        </select>
      </div>

      {/* Reached ONLY on a request that came back clean and carried nothing. A failed fetch returned
          above, so no authorization claim is printed over a transport error — and none is printed
          here either: `No companies found` (common.json, en/ar/fr) states the fact and stops, because
          on this branch the reader demonstrably IS an Operator. The old sentence's second clause was
          only ever true for the 403 the panel above now owns. */}
      {!loadingCompanies && companies.length === 0 && (
        <div className="py-12 text-center text-muted-foreground">
          <p>{t('No companies found')}</p>
        </div>
      )}

      {/* 🔴 `key` IS LOAD-BEARING, AND BUYS EXACTLY ONE THING. Changing the picker REMOUNTS the tab,
          so the previous company's fetched `rules` object cannot survive the switch: the new instance
          starts at `rules === null`, and handleSave early-returns until its own GET has landed. That
          closes the cross-tenant variant — a failed SECOND fetch leaving company A's whole
          CompanyRules row in state under company B's form, where the first toggle re-arms Save and
          posts A's row to B.

          ⚠️ IT NEVER MADE "ERROR BANNER WITH THE FORM STILL BELOW" UNREACHABLE. An earlier version of
          this comment claimed that, and it was false: on a FIRST-fetch failure the tab rendered its
          banner with `rules === null`, `noRulesRow === false` and EMPTY_FLAGS — the full form, three
          innocent OFF switches for a tenant whose real flags were never read, and a toggle that
          re-armed a Save whose handler early-returned on `!rules` with no request and no feedback. A
          remount cannot help there, because there is no previous state to discard. That was
          EngagementJobsTab's defect and it is FIXED IN THAT FILE, not here: its render gate is now
          `companyId && rules && !noRulesRow`, the exact negation of handleSave's precondition, and its
          error path clears `rules` and the flags. Do not read this `key` as ever having covered it,
          and do not relax that gate back to `companyId && !noRulesRow`.

          §14.1c/15 — NO `aiNudgesReadOnly` PROP IS PASSED, BECAUSE THERE IS NO LONGER ONE TO PASS.
          The read-only lock this page invented is reversed: EngagementJobsTab now takes `companyId`
          alone, derives its operator mode from that single discriminator, and leaves the AI-nudges
          master WRITABLE (`disabled={saving}`, no lock term). `companyId` is therefore the whole
          contract — do not reintroduce a lock prop here, and do not re-add one there.
          ⚠️ The consequence is real and is handled by MARKING, not by disabling: the master switch
          writes Company.EnableAINudges for another tenant, and that column AND-gates both AI senders,
          so one click can un-mute or mute a whole tenant's AI-nudge family. The tab marks that at the
          point of action ("Operator action" chip, plus an "Unsaved changes" line while the master
          differs from its baseline). A fuller sentence naming the effect is blocked on ar/fr copy that
          does not exist, and no Arabic or French may be hand-authored. */}
      {selectedCompanyId !== null && (
        <EngagementJobsTab key={selectedCompanyId} companyId={selectedCompanyId} />
      )}
    </PageLayout>
  );
}
